/**
 * Per-person memory of a page choice, kept in browser localStorage keyed by
 * user id (kanban 02f7bacc for the Orders filter, 778fe5cf for the inbox tab).
 *
 * The memory follows a person on the machine they use and never leaks across
 * accounts on a shared one. Every read and write is wrapped: a blocked or
 * throwing store degrades to "nothing remembered", never to a crash. Values
 * are validated on the way out, so a stale or hand-edited entry is ignored.
 *
 * Each page keeps its own module (orderFilterMemory.ts, inboxTabMemory.ts)
 * holding the value type and the precedence rule for what the page opens on;
 * this module is only the storage behind them.
 */

export interface PerUserMemory<T extends string> {
  /** The localStorage key for one user. Exposed so tests can seed it. */
  storageKey(userId: string): string;
  /** The value this user last chose on this browser, or null if none / invalid. */
  read(userId: string | null | undefined): T | null;
  /** Remember the value this user just chose. Silent when storage is unavailable. */
  remember(userId: string | null | undefined, value: T): void;
}

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    // Access itself can throw (sandboxed iframe, blocked storage).
    return null;
  }
}

export function createPerUserMemory<T extends string>(opts: {
  storagePrefix: string;
  isValid: (v: string | null | undefined) => v is T;
}): PerUserMemory<T> {
  const storageKey = (userId: string): string => `${opts.storagePrefix}${userId}`;

  return {
    storageKey,
    read(userId) {
      if (!userId) return null;
      const store = storage();
      if (!store) return null;
      try {
        const raw = store.getItem(storageKey(userId));
        return opts.isValid(raw) ? raw : null;
      } catch {
        return null;
      }
    },
    remember(userId, value) {
      if (!userId) return;
      const store = storage();
      if (!store) return;
      try {
        store.setItem(storageKey(userId), value);
      } catch {
        // Quota or blocked storage: the page still works, it just forgets.
      }
    },
  };
}
